import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBillingInfo } from "../data/useBillingInfo";
import { useDefaultLandingRoute } from "../hooks/useDefaultLandingRoute";
import { ROUTE } from "../routes";
import { BillingInfo } from "../types/billingInfo";

type BillingFeatureFlag =
  | "applications_enabled"
  | "screening_enabled"
  | "monitoring_enabled"
  | "profiles_enabled";

/**
 * Gates a module's routes on the tenant's subscription flag and, when
 * `permission` is given, the user's view permission for that module.
 * - Flag off → NotFound (or `redirectTo`).
 * - Permission missing → the user's default landing module, so the page (and
 *   its list request, which would 403 and log them out) never mounts.
 * UX gating only — the backend still enforces every permission.
 */
const FeatureRouteGuard = ({
  flag,
  permission,
  defaultEnabled = false,
  redirectTo,
  children,
}: {
  flag: BillingFeatureFlag;
  permission?: string;
  defaultEnabled?: boolean;
  redirectTo?: string;
  children: React.ReactNode;
}) => {
  const { hasPermission, isLoadingUserDetails } = useAuth();
  const { data: billingData, isLoading } = useBillingInfo();
  const { defaultRoute } = useDefaultLandingRoute();

  if (isLoading || (permission && isLoadingUserDetails)) return null;

  const results = billingData?.data?.results as BillingInfo[] | undefined;
  const enabled = results?.[0]?.[flag] ?? defaultEnabled;

  if (!enabled) {
    return <Navigate to={redirectTo ?? `/${ROUTE.NOT_FOUND}`} replace />;
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to={defaultRoute} replace />;
  }

  return <>{children}</>;
};

export default FeatureRouteGuard;
